<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { useUiStore } from '@/stores/ui';
import BaseButton from './BaseButton.vue';
import BaseModal from './BaseModal.vue';

/**
 * Renders whatever `ui.confirm()` is currently awaiting.
 *
 * Mounted once in the shell, so every destructive action in the app goes through
 * the same dialog and none of them can forget to ask.
 *
 * Being mounted in the shell is also exactly why `tier="confirm"` below is not
 * optional. Most callers of `ui.confirm()` are themselves inside a modal — the
 * comment job detail dialog asking to approve a real comment on a stranger's
 * post, the project form asking to drop a keyword. This host mounts at app boot
 * and they mount later, so on any tie the newer modal wins the paint order and
 * buries the question. The tier says "above ordinary modals" instead of relying
 * on who happened to mount first.
 */

const ui = useUiStore();
const { pendingConfirm } = storeToRefs(ui);
</script>

<template>
  <BaseModal
    :open="pendingConfirm !== null"
    :title="pendingConfirm?.title ?? ''"
    width="440px"
    persistent
    tier="confirm"
    @close="ui.resolveConfirm(false)"
  >
    <p class="message">{{ pendingConfirm?.message }}</p>

    <template #footer>
      <BaseButton @click="ui.resolveConfirm(false)">
        {{ pendingConfirm?.cancelLabel ?? 'Huỷ' }}
      </BaseButton>
      <BaseButton
        :variant="pendingConfirm?.danger ? 'danger' : 'primary'"
        @click="ui.resolveConfirm(true)"
      >
        {{ pendingConfirm?.confirmLabel ?? 'Xác nhận' }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<style scoped>
.message {
  margin: 0;
  white-space: pre-line;
}
</style>
